<template>
  <div class="video-picker">
    <header class="bar">
      <h1>
        Insert existing videos
      </h1>

      <section class="sort-options">
        <p :class="{'selected': selectedSortIndex === 0}" @click="sortReverseChronologically">New → Old</p>
        <p :class="{'selected': selectedSortIndex === 1}" @click="sortChronologically">Old → New</p>
        <p :class="{'selected': selectedSortIndex === 2}" @click="sortAlphabetically">A → Z</p>
        <p :class="{'selected': selectedSortIndex === 3}" @click="sortReverseAlphabetically">Z → A</p>
      </section>

      <div>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/><path d="M0 0h24v24H0z" fill="none"/></svg>
        <input
          type="text"
          v-model="searchTerm"
          placeholder="Search by title"
          class="cf-form-input"
        >
      </div>
    </header>

    <transition name="fade">

      <div v-show="this.$store.state.isReady">
        <div v-show="videosToRender.length > 0">
          <div class="video-grid-padding">
            <transition-group
              name="sorting-animation"
              tag="section"
              class="video-grid"
              v-on:before-leave="beforeLeave"
              ref="videoGrid"
            >
              <VideoTile
                class="sorting-animation-item"
                v-for="v in videosToRender"
                :key="v.uuid"
                v-bind="v"
                @click="flipSelectionStatus(v.uuid)"
              />
            </transition-group>
          </div>
        </div>

        <div v-if="videosToRender.length === 0">
          <div class="no-content">
            There are no videos.
          </div>
        </div>
      </div>

    </transition>

    <div class="button-row bar">
      <div>
        <button class="cf-btn-primary btn-cta" @click="addVideos" :disabled="this.$store.getters.selectedVideos.length === 0">
          Insert videos
        </button>
      </div>
      <div>
        <button class="cf-btn-secondary" @click="closeWindow">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import VideoTile from "./VideoTile";

export default {
  name: "VideoPicker",
  components: {
    VideoTile
  },
  data() {
    return {
      selectedSortIndex: 0,
      isAscendingSort: false,
      sortKey: 'createdAt',
      searchTerm: '',
      searchTermDelayed: '', // To trigger transition properly, this value is updated 1 frame after
    };
  },
  watch: {
    searchTerm(val) {
      // Record for current positions of VideoTiles, to use with position: absolute later
      this.recordVideoTilePosition();

      // Update searchTerm now, and searchTermDelayed after the VideoTiles' position is recorded
      this.searchTerm = val;
      this.$nextTick(() => {
        this.searchTermDelayed = val;
      });
    },
  },
  computed: {
    videosToRender() {
      // Create a copy of array or filter by searchTermDelayed
      let videos = [];
      if (this.searchTermDelayed.length === 0) {
        videos = this.$store.state.videos.concat();
      } else {
        const s = this.searchTermDelayed.toLowerCase();
        videos = this.$store.state.videos.filter(v => v.title.toLowerCase().indexOf(s) !== -1);
      }

      // Sort and return videos
      const sortOrder = this.isAscendingSort ? 1 : -1;
      return videos.sort((a, b) => {
        // Compare two videos
        let value = 0;
        if (this.sortKey === 'createdAt') {
          value = new Date(a.createdAt) - new Date(b.createdAt);
        } else if (this.sortKey === 'title') {
          value = a.title.localeCompare(b.title);
        }

        // Handle ascending / descending order
        return value * sortOrder;
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$el.style.opacity = 1;
    });
    this.$store.dispatch('loadAllVideos');
  },
  methods: {
    flipSelectionStatus(uuid) {
      this.$store.commit('flipSelection', uuid);
    },
    addVideos() {
      const selectedVideos = this.$store.getters.selectedVideos;
      this.$extension.close(selectedVideos);
    },
    closeWindow() {
      this.$extension.close([]);
    },

    sortReverseChronologically() {
      this.selectedSortIndex = 0;
      this.isAscendingSort = false;
      this.sortKey = 'createdAt';
    },
    sortChronologically() {
      this.selectedSortIndex = 1;
      this.isAscendingSort = true;
      this.sortKey = 'createdAt';
    },
    sortAlphabetically() {
      this.selectedSortIndex = 2;
      this.isAscendingSort = true;
      this.sortKey = 'title';
    },
    sortReverseAlphabetically() {
      this.selectedSortIndex = 3;
      this.isAscendingSort = false;
      this.sortKey = 'title';
    },

    beforeLeave(el) {
      const left = el.dataset.leftOffset;
      const top = el.dataset.topOffset;

      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
    },
    recordVideoTilePosition() {
      const {videoGrid} = this.$refs;
      const {left, top} = videoGrid.$el.getBoundingClientRect();
      for (const videoTile of videoGrid.children) {
        if (videoTile.componentInstance) {
          videoTile.componentInstance.recordCurrentPosition(left, top);
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
  .video-picker {
    height: 790px;
    position: relative;

    opacity: 0;
    transition: opacity .5s;
  }

  .bar {
    position: fixed;
    z-index: 4096;
    right: 0;
    left: 0;
    display: flex;
    align-items: center;
    height: 64px;
    border-bottom: 1px solid #b4c3ca;
    border-top: 1px solid #b4c3ca;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.09);
    padding-left: 2em;
    padding-right: 2em;
    background-color: rgb(247, 249, 250);
  }

  .bar {
    header {
      top: 0;
    }

    h1 {
      font-size: 20px;
      font-weight: 700;
    }
  }

  header.bar {
    display: flex;
    justify-content: space-between;

    h1, section, div {
      flex: initial;
    }

    .sort-options {
      padding-top: 4px;
      padding-left: 30px;
      p {
        display: inline-block;
        margin: 0 15px;
        color: #8091a5;
        user-select: none;
        cursor: pointer;
      }
      p.selected {
        color: #4a90e2;
        font-weight: 700;
      }
    }

    div {
      display: flex;
      align-items: center;
    }
    svg {
      flex: initial;
      margin-right: 5px;
      fill: #8091a5;
    }
    input {
      flex: initial;
      width: 200px;
    }
    input:focus {
      outline: none;
    }

    input::placeholder {
      color: #8091a5;
    }
  }

  .sorting-animation-move {
    transition: all 550ms;
    z-index: 1;
  }

  .sorting-animation-enter, .sorting-animation-leave-to {
    opacity: 0;
    transform: scale(0.95);
  }

  .sorting-animation-enter-active, .sorting-animation-leave-active {
    transition: all 250ms;
  }

  .sorting-animation-leave-active {
    position: absolute;
    z-index: 0;
  }

  .video-grid-padding {
    padding-top: 64px;
  }

  .video-grid {
    position: relative;
    height: 662px;
    padding-bottom: 24px;
    padding-left: 37px;
    display: flex;
    flex-wrap: wrap;
    justify-content: left;
    align-content: flex-start;
    overflow: scroll;

    .spacer {
      flex: auto;
    }
  }

  .no-content {
    text-align: center;
    padding-top: 375px;
  }

  .button-row {
    bottom: 0;
    display: flex;
    justify-content: center;

    div {
      flex: initial;
      margin: 0 10px;
    }

    label {
      user-select: none;
      width: 150px;
    }

    button {
      font-family: "Avenir Next W01", Helvetica, sans-serif;
    }

    .btn-cta {
      border-color: #0baa75;
      background-image: -webkit-gradient(linear,left bottom,left top,from(#0eb87f),to(#14d997));
      background-image: linear-gradient(0deg,#0eb87f,#14d997);
      background-size: 100% 200%;
    }
  }
</style>
